/*
 * Encrypted notification previews for end-to-end encrypted chats (docs/E2EE_ARCHITECTURE.md §6a).
 *
 * The server cannot read an encrypted message, so on its own a push can only say "New message". Like WhatsApp's
 * and Signal's notification extensions, the device does the reading: every device that takes notifications holds
 * a push-preview key pair, and the sender's device encrypts a one-line preview for each recipient device.
 *
 *  - Key: ECDH P-256 made with WebCrypto, the private key NON-extractable, kept in IndexedDB where this device's
 *    service worker reads it (public/sw-preview.js). P-256 and not X25519: X25519 in WebCrypto came late and
 *    unevenly (Safari and older Chrome/Android WebViews), and a phone whose browser lacks it would never get a
 *    preview; P-256 ECDH works in every browser that has Web Push. The public key is registered with the server,
 *    signed by this device's own end-to-end encryption key (Ed25519, cross-signed by the owner's identity), and
 *    renewed with each new push subscription.
 *  - Senders encrypt only for devices their crypto already trusts for room keys (signed by their owner) whose
 *    preview key carries that device's valid signature: the server cannot add a key of its own and read previews.
 *  - Envelope (version 1, base64url): 0x01 | keyId (4 bytes: SHA-256 of the recipient's public key) | the
 *    ephemeral public key (65) | IV (12) | AES-256-GCM ciphertext. Key = HKDF-SHA-256(ECDH(ephemeral, recipient),
 *    salt = ephemeral ‖ recipient public key, info = "dijitalapp push preview v1"). AAD = the 70-byte header ‖
 *    "dijitalapp-pp-v1\n<conversationId>\n<clientMsgId>\n<recipient deviceId>\n<sender userId>", so a preview
 *    opens only in the push of that message, in that chat, on that device, from that sender.
 *  - The plaintext is PreviewData (lib/notifyPreview.ts), worded on the recipient's device in its own language.
 */
import type { DecryptedContent } from './e2ee';
import { api } from './api';
import { buildPreview, type PreviewData } from './notifyPreview';

export const PREVIEW_DB = 'dijitalapp-push-preview';
export const PREVIEW_STORE = 'keys';
export const PREVIEW_RECORD = 'current';
const INFO = 'dijitalapp push preview v1';
const AAD_LABEL = 'dijitalapp-pp-v1';
/** The server keeps at most this much of one preview (push/previews.go MaxPreviewChars). */
export const MAX_CIPHERTEXT_CHARS = 1024;
/** Re-sent to the server at most once a day (it may have dropped it: a sweep, a sign-out elsewhere). */
const UPLOAD_EVERY_MS = 24 * 60 * 60 * 1000;
/** How long a chat's device keys are reused before they are fetched again. */
const KEYS_TTL_MS = 5 * 60 * 1000;
/** Previews never hold a message up: past this they are left out (the push says "New message"). */
const BUILD_TIMEOUT_MS = 2500;

/** What this device keeps (IndexedDB): its preview key pair, for its device and push subscription. */
export interface PreviewKeyRecord {
  deviceId: string;
  userId: string;
  /** Non-extractable: usable for ECDH here and in the service worker, never readable. */
  privateKey: CryptoKey;
  /** Raw uncompressed point, base64url. */
  publicKey: string;
  /** The push subscription the key was made for: a new one brings a new key. */
  endpoint: string;
  createdAt: number;
  uploadedAt?: number;
}

export interface PreviewContext {
  conversationId: string;
  /** The message's clientMsgId (known before it is sent; the server puts it into the push as `ref`). */
  ref: string;
  /** The recipient device. */
  deviceId: string;
  /** The sender's user id. */
  from: string;
}

const enc = new TextEncoder();

export function b64url(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function fromB64(text: string): Uint8Array<ArrayBuffer> {
  const std = text.replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(std + '='.repeat((4 - (std.length % 4)) % 4));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function concat(...parts: Uint8Array[]): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(new ArrayBuffer(parts.reduce((n, p) => n + p.length, 0)));
  let at = 0;
  for (const p of parts) {
    out.set(p, at);
    at += p.length;
  }
  return out;
}

export function aadFor(header: Uint8Array, c: PreviewContext): Uint8Array<ArrayBuffer> {
  return concat(header, enc.encode(`${AAD_LABEL}\n${c.conversationId}\n${c.ref}\n${c.deviceId}\n${c.from}`));
}

export async function keyIdOf(publicRaw: Uint8Array<ArrayBuffer>, subtle: SubtleCrypto = crypto.subtle): Promise<Uint8Array> {
  return new Uint8Array(await subtle.digest('SHA-256', publicRaw)).slice(0, 4);
}

/** ECIES: the preview encrypted for one device's public key (base64url envelope). */
export async function encryptPreview(recipientPublicKey: string, data: PreviewData, c: PreviewContext, subtle: SubtleCrypto = crypto.subtle): Promise<string> {
  const recipientRaw = fromB64(recipientPublicKey);
  if (recipientRaw.length !== 65 || recipientRaw[0] !== 4) throw new Error('bad preview key');
  const recipient = await subtle.importKey('raw', recipientRaw, { name: 'ECDH', namedCurve: 'P-256' }, false, []);
  const eph = (await subtle.generateKey({ name: 'ECDH', namedCurve: 'P-256' }, false, ['deriveBits'])) as CryptoKeyPair;
  const ephRaw = new Uint8Array(await subtle.exportKey('raw', eph.publicKey));
  const shared = await subtle.deriveBits({ name: 'ECDH', public: recipient }, eph.privateKey, 256);
  const hkdf = await subtle.importKey('raw', shared, 'HKDF', false, ['deriveKey']);
  const key = await subtle.deriveKey({ name: 'HKDF', hash: 'SHA-256', salt: concat(ephRaw, recipientRaw), info: enc.encode(INFO) }, hkdf, { name: 'AES-GCM', length: 256 }, false, ['encrypt']);
  const header = concat(new Uint8Array([1]), await keyIdOf(recipientRaw, subtle), ephRaw);
  const iv = crypto.getRandomValues(new Uint8Array(new ArrayBuffer(12)));
  const ct = new Uint8Array(await subtle.encrypt({ name: 'AES-GCM', iv, additionalData: aadFor(header, c), tagLength: 128 }, key, enc.encode(JSON.stringify(data))));
  return b64url(concat(header, iv, ct));
}

/** What the device signs to vouch for its preview key (checked by senders with its Ed25519 key). */
export function keyStatement(userId: string, deviceId: string, publicKey: string): string {
  return `dijitalapp-push-preview-key:v1\n${userId}\n${deviceId}\n${publicKey}`;
}

/** Ed25519 check with WebCrypto (false where the browser lacks it: then no preview is encrypted for that device). */
export async function verifyStatement(ed25519: string, signature: string, statement: string, subtle: SubtleCrypto = crypto.subtle): Promise<boolean> {
  try {
    const key = await subtle.importKey('raw', fromB64(ed25519), { name: 'Ed25519' }, false, ['verify']);
    return await subtle.verify({ name: 'Ed25519' }, key, fromB64(signature), enc.encode(statement));
  } catch {
    return false;
  }
}

// ---------- this device's key (IndexedDB, shared with the service worker) ----------

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(PREVIEW_DB, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(PREVIEW_STORE)) req.result.createObjectStore(PREVIEW_STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('indexedDB'));
  });
}

async function withStore<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDb();
  try {
    return await new Promise<T>((resolve, reject) => {
      const tx = db.transaction(PREVIEW_STORE, mode);
      const req = fn(tx.objectStore(PREVIEW_STORE));
      tx.oncomplete = () => resolve(req.result);
      tx.onerror = () => reject(tx.error ?? new Error('indexedDB'));
      tx.onabort = () => reject(tx.error ?? new Error('indexedDB'));
    });
  } finally {
    db.close();
  }
}

export async function readKeyRecord(): Promise<PreviewKeyRecord | null> {
  try {
    return ((await withStore('readonly', (s) => s.get(PREVIEW_RECORD))) as PreviewKeyRecord | undefined) ?? null;
  } catch {
    return null;
  }
}

/** Forgets this device's preview key here (signing out, notifications off); the server deletes its copy itself. */
export async function clearPreviewKey(): Promise<void> {
  try {
    await withStore('readwrite', (s) => s.delete(PREVIEW_RECORD));
  } catch {
    /* no IndexedDB: nothing was kept */
  }
}

export async function newKeyRecord(userId: string, deviceId: string, endpoint: string): Promise<PreviewKeyRecord> {
  // extractable: false — the private key can be used (here and by the service worker), never read out.
  const pair = (await crypto.subtle.generateKey({ name: 'ECDH', namedCurve: 'P-256' }, false, ['deriveBits'])) as CryptoKeyPair;
  const publicKey = b64url(new Uint8Array(await crypto.subtle.exportKey('raw', pair.publicKey)));
  return { userId, deviceId, privateKey: pair.privateKey, publicKey, endpoint, createdAt: Date.now() };
}

let syncing: Promise<void> | null = null;

/**
 * Makes sure this device has a preview key for its current push subscription and the server has it, signed by
 * this device's end-to-end encryption key. Quietly does nothing without a subscription, IndexedDB or WebCrypto.
 */
export function syncPreviewKey(o: { userId: string; deviceId: string; sign: (text: string) => Promise<string | null> }): Promise<void> {
  syncing ??= (async () => {
    try {
      if (typeof indexedDB === 'undefined' || !('serviceWorker' in navigator) || !crypto?.subtle) return;
      const reg = await navigator.serviceWorker.getRegistration('/');
      const sub = await reg?.pushManager.getSubscription();
      if (!sub) return;
      let rec = await readKeyRecord();
      if (!rec || rec.deviceId !== o.deviceId || rec.userId !== o.userId || rec.endpoint !== sub.endpoint) {
        rec = await newKeyRecord(o.userId, o.deviceId, sub.endpoint);
        const fresh = rec;
        await withStore('readwrite', (s) => s.put(fresh, PREVIEW_RECORD));
      }
      if (rec.uploadedAt && Date.now() - rec.uploadedAt < UPLOAD_EVERY_MS) return;
      const signature = await o.sign(keyStatement(o.userId, o.deviceId, rec.publicKey));
      if (!signature) return;
      await api('PUT', '/push/preview-key', { publicKey: rec.publicKey, signature });
      const done = { ...rec, uploadedAt: Date.now() };
      await withStore('readwrite', (s) => s.put(done, PREVIEW_RECORD));
    } catch {
      /* tried again on the next start */
    } finally {
      syncing = null;
    }
  })();
  return syncing;
}

// ---------- senders: the chat's device keys, and the previews ----------

interface ServerKey {
  userId: string;
  deviceId: string;
  publicKey: string;
  signature: string;
}

const keyCache = new Map<string, { at: number; keys: ServerKey[]; max: number }>();
const verified = new Map<string, boolean>();

async function chatKeys(conversationId: string): Promise<{ keys: ServerKey[]; max: number }> {
  const hit = keyCache.get(conversationId);
  if (hit && Date.now() - hit.at < KEYS_TTL_MS) return hit;
  const r = await api<{ keys: ServerKey[]; max: number }>('GET', `/conversations/${conversationId}/push-preview-keys`);
  const entry = { at: Date.now(), keys: r.keys, max: r.max };
  keyCache.set(conversationId, entry);
  return entry;
}

/** A chat's members or their devices changed: its keys are fetched again on the next message. */
export function forgetChatKeys(conversationId?: string): void {
  if (conversationId) keyCache.delete(conversationId);
  else keyCache.clear();
}

export interface PreviewCrypto {
  /** "userId/deviceId" → that device's Ed25519 key, for devices signed by their owner (E2ee.signedDeviceKeys). */
  signedDeviceKeys(userIds: string[]): Promise<Map<string, string>>;
}

/** The encrypted previews of one message for the chat's other devices (best effort: [] on any trouble). */
export async function makePushPreviews(
  crypto: PreviewCrypto,
  o: { conversationId: string; ref: string; from: string; content: DecryptedContent; reply?: boolean; viewOnce?: boolean },
): Promise<{ deviceId: string; ciphertext: string }[]> {
  if (o.viewOnce) return [];
  const work = (async () => {
    const data = buildPreview(o.content, o.reply ? { reply: true } : {});
    if (!data) return [];
    const { keys, max } = await chatKeys(o.conversationId);
    if (keys.length === 0 || keys.length > max) return [];
    const signed = await crypto.signedDeviceKeys([...new Set(keys.map((k) => k.userId))]);
    const out: { deviceId: string; ciphertext: string }[] = [];
    for (const k of keys) {
      const ed = signed.get(`${k.userId}/${k.deviceId}`);
      if (!ed) continue;
      const id = `${ed}|${k.publicKey}|${k.signature}`;
      let ok = verified.get(id);
      if (ok === undefined) {
        ok = await verifyStatement(ed, k.signature, keyStatement(k.userId, k.deviceId, k.publicKey));
        verified.set(id, ok);
      }
      if (!ok) continue;
      const c = { conversationId: o.conversationId, ref: o.ref, deviceId: k.deviceId, from: o.from };
      let ciphertext = await encryptPreview(k.publicKey, data, c);
      // A long name or text in many-byte letters: a shorter line rather than none.
      if (ciphertext.length > MAX_CIPHERTEXT_CHARS && (data.t || data.n)) {
        const short: PreviewData = { ...data, ...(data.t ? { t: shorten(data.t) } : {}), ...(data.n ? { n: shorten(data.n) } : {}) };
        ciphertext = await encryptPreview(k.publicKey, short, c);
      }
      if (ciphertext.length <= MAX_CIPHERTEXT_CHARS) out.push({ deviceId: k.deviceId, ciphertext });
    }
    return out;
  })();
  const timeout = new Promise<{ deviceId: string; ciphertext: string }[]>((resolve) => setTimeout(() => resolve([]), BUILD_TIMEOUT_MS));
  try {
    return await Promise.race([work, timeout]);
  } catch {
    return [];
  }
}

function shorten(text: string): string {
  return text.length > 40 ? `${text.slice(0, 39)}…` : text;
}
